import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toEmbedUrl } from "@/lib/video";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, RotateCcw, CheckCircle2, AlertOctagon, HelpCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import Classification from "@/components/Classification";

export default function GuidedTroubleshooting() {
  const { id } = useParams();
  const [flow, setFlow] = useState(null);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/content/flows/${id}`).then((r) => { setFlow(r.data); setCurrent(r.data.start); }).catch(() => {});
  }, [id]);

  if (!flow) return <div className="text-slate-500" data-testid="guided-loading">Loading…</div>;

  const node = flow.nodes[current];
  const restart = () => { setCurrent(flow.start); setHistory([]); };
  const goto = (next, label) => { setHistory((h) => [...h, { label }]); setCurrent(next); };
  const back = () => {
    if (history.length === 0) return;
    // simplistic: restart then not really; instead just restart to keep it robust
    restart();
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="guided-page">
      <Link to="/troubleshooting" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-mlily-blue" data-testid="guided-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to troubleshooting
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-mlily-blue">Guided · {flow.category}</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-1">{flow.title}</h1>
        </div>
        <Button variant="outline" size="sm" onClick={restart} className="gap-1" data-testid="guided-restart-btn">
          <RotateCcw className="w-4 h-4" /> Restart
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Classification value={flow.classification} />
      </div>
      {flow.model_warning && (
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-mlily-blue" data-testid="guided-model-warning">
          <Info className="w-4 h-4 mt-0.5 shrink-0" /> {flow.model_warning}
        </div>
      )}

      {history.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs text-slate-500">
          {history.map((h, i) => (
            <span key={i} className="px-2 py-1 rounded-full bg-slate-100">{h.label}</span>
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div key={current} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
          {node.type === "question" ? (
            <Card className="p-6" data-testid="guided-question">
              <div className="flex items-center gap-2 text-mlily-blue mb-3">
                <HelpCircle className="w-5 h-5" />
                <span className="text-xs uppercase tracking-wider font-bold">Question</span>
              </div>
              <h2 className="font-heading text-xl font-bold text-slate-900">{node.text}</h2>
              {node.checks && (
                <p className="text-sm text-slate-600 mt-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{node.checks}</p>
              )}
              {node.steps?.length > 0 && (
                <ol className="mt-3 space-y-2">
                  {node.steps.map((s, i) => (
                    <li key={i} className="flex gap-3 text-sm text-slate-700">
                      <span className="w-6 h-6 rounded-full bg-mlily-blue text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              )}
              {node.video_url && (
                <div className="mt-4 aspect-video rounded-lg overflow-hidden bg-black">
                  <iframe title="step-video" src={toEmbedUrl(node.video_url)} className="w-full h-full" allowFullScreen />
                </div>
              )}
              <div className="grid gap-3 mt-6">
                {node.answers.map((ans, i) => {
                  const isYes = ans.label.toUpperCase() === "YES";
                  const isNo = ans.label.toUpperCase() === "NO";
                  return (
                    <Button key={i} variant="outline" onClick={() => goto(ans.next, ans.label)}
                      data-testid={`guided-answer-${i}`}
                      className={cn("justify-start h-auto py-3 text-left border-2",
                        isYes && "hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-700",
                        isNo && "hover:border-red-500 hover:bg-red-50 hover:text-red-700",
                        !isYes && !isNo && "hover:border-mlily-blue hover:bg-blue-50 hover:text-mlily-blue")}>
                      {ans.label}
                    </Button>
                  );
                })}
              </div>
            </Card>
          ) : (
            <Card className={cn("p-6 border-2", node.outcome_type === "resolved" ? "border-emerald-500 bg-emerald-50/40" : "border-red-500 bg-red-50/40")} data-testid="guided-outcome">
              <div className={cn("flex items-center gap-2 mb-3", node.outcome_type === "resolved" ? "text-emerald-700" : "text-red-700")}>
                {node.outcome_type === "resolved" ? <CheckCircle2 className="w-6 h-6" /> : <AlertOctagon className="w-6 h-6" />}
                <span className="font-heading text-lg font-bold">{node.text}</span>
              </div>
              {node.body && <p className="text-sm text-slate-700 whitespace-pre-line">{node.body}</p>}
              {node.customer_response && (
                <div className="mt-4 rounded-lg bg-white border border-emerald-200 p-3">
                  <div className="text-xs uppercase tracking-wider font-bold text-emerald-600">What to tell the customer</div>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{node.customer_response}</p>
                </div>
              )}
              {node.escalation_instructions && (
                <div className="mt-4 rounded-lg bg-white border border-red-200 p-3">
                  <div className="text-xs uppercase tracking-wider font-bold text-red-600">Escalation instructions</div>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{node.escalation_instructions}</p>
                </div>
              )}
              {node.video_url && (
                <div className="mt-4 aspect-video rounded-lg overflow-hidden bg-black">
                  <iframe title="outcome-video" src={toEmbedUrl(node.video_url)} className="w-full h-full" allowFullScreen />
                </div>
              )}
              <Button onClick={restart} className="mt-5 bg-mlily-blue hover:bg-mlily-bluehover gap-1" data-testid="guided-outcome-restart">
                <RotateCcw className="w-4 h-4" /> Start over
              </Button>
            </Card>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
